import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useBoolean } from "react-hanger";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import {
  DataGrid,
  GridColDef,
} from "@mui/x-data-grid";

import { siteConstants } from "../../../constants";
import { alertActions, csActions, hrcActions } from "../../../redux/actions";
import GridCellExpand from "../../GridCellExpand";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const getDropzoneBackgroundColor = (props) => {
  if (props.isDragActive)
    return "#fafafa";

  return "#ebebeb";
}

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-style: dashed;
  border-color: #aaaaaa;
  background-color: ${props => getDropzoneBackgroundColor(props)};
  color: #8a8a8a;
  outline: none;
`;

const StyledDropzone = (props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    maxFiles: 1,
    multiple: false,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0)
      props.onFileAccepted(acceptedFiles[0]);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections.length > 0)
      props.onFileRejected();
  }, [fileRejections]);

  return (
    <>
      <DropzoneContainer {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <Typography align="center" variant="subtitle2">
          <p>Drag 'n' drop guidance database file here, or click to select file</p>
        </Typography>
        <CloudUploadIcon />
      </DropzoneContainer>
      {acceptedFiles.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {acceptedFiles[0].name} ({(acceptedFiles[0].size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
      {fileRejections.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {fileRejections[0].file.name} ({(fileRejections[0].file.size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
    </>
  )
}

const CoupleGuidanceDatabasePage = (props) => {
  const [themeGroupGuidance, setThemeGroupGuidance] = useState([]);
  const [talentThemeGuidance, setTalentThemeGuidance] = useState([])
  const [guidanceFile, setGuidanceFile] = useState(null);
  const isConfirmationDialogOpen = useBoolean(false);

  const renderCellExpand = (params) => {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={"40%"}
      />
    );
  }

  const themeGroupColumns: GridColDef[] = [
    { field: "theme_group", headerName: "Theme Group", width: 120, },
    {
      field: "guidance_1",
      headerName: "P1 = 0",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_2",
      headerName: "5 <= P1 <= 9",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_3",
      headerName: "3 <= P1 <= 4",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_4",
      headerName: "0 <= P1 <= 2",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_5",
      headerName: "P2 = 0",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_6",
      headerName: "5 <= P2 <= 9",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_7",
      headerName: "3 <= P2 <= 4",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_8",
      headerName: "0 <= P2 <= 2",
      flex: 1,
      renderCell: renderCellExpand,
    },
  ];

  const talentThemeColumns: GridColDef[] = [
    { field: "partner_talent_theme", headerName: "Partner Talent Theme", width: 200, },
    { field: "theme_group", headerName: "Theme Group", width: 120 },
    {
      field: "guidance_1",
      headerName: "1 <= Mine <= 10",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_2",
      headerName: "11 <= Mine <= 29",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "guidance_3",
      headerName: "30 <= Mine <= 34",
      flex: 1,
      renderCell: renderCellExpand,
    },
  ];

  const onFileAccepted = (file) => {
    props.alertReset();
    setGuidanceFile(file);
  }

  const onFileRejected = () => {
    props.alertError(siteConstants.ERROR_MSG_INVALID_GUIDANCE_DATABASE_FILE);
  }

  const handleConfirmationDialogClose = () => {
    isConfirmationDialogOpen.setFalse();
  }

  const handleContinueButtonClick = () => {
    isConfirmationDialogOpen.setFalse();
    props.updateGuidance(guidanceFile);
  }

  useEffect(() => {
    if (guidanceFile)
      isConfirmationDialogOpen.setTrue();
  }, [guidanceFile]);

  useEffect(() => {
    const _themeGroupGuidance = [];
    const _talentThemeGuidance = [];
    if (props.guidance) {
      props.guidance.theme_group_guidance.map((row) => {
        _themeGroupGuidance.push({
          id: row.id,
          theme_group: row.theme_group,
          guidance_1: row.guidance_1,
          guidance_2: row.guidance_2,
          guidance_3: row.guidance_3,
          guidance_4: row.guidance_4,
          guidance_5: row.guidance_5,
          guidance_6: row.guidance_6,
          guidance_7: row.guidance_7,
          guidance_8: row.guidance_8,
        })
      });
      props.guidance.talent_theme_guidance.map((row) => {
        _talentThemeGuidance.push({
          id: row.id,
          partner_talent_theme: row.partner_talent_theme,
          theme_group: row.theme_group,
          guidance_1: row.guidance_1,
          guidance_2: row.guidance_2,
          guidance_3: row.guidance_3,
        })
      })
    }
    setThemeGroupGuidance(_themeGroupGuidance);
    setTalentThemeGuidance(_talentThemeGuidance);
  }, [props.guidance]);

  useEffect(() => {
    props.alertReset();
    props.listGuidance();
  }, []);

  return (
    <>
      <Helmet>
        <title>HRC Guidance Database</title>
      </Helmet>

      <Container
        maxWidth="xl"
        sx={{
          marginTop: "30px",
          textAlign: "left",
        }}
      >
        <Typography variant="h4">HRC Guidance Database</Typography>
        {
          props.alert.message &&
          <Alert variant="outlined" severity={ props.alert.type }>
            { props.alert.message }
          </Alert>
        }
        <StyledDropzone
          selectedFilePath={props.selectedFilePath}
          selectedFileSize={props.selectedFileSize}
          onFileAccepted={onFileAccepted}
          onFileRejected={onFileRejected}
        />
        <Typography variant="h6" sx={{ mt: 3 }}>Theme Groups</Typography>
        <DataGrid
          rows={themeGroupGuidance}
          columns={themeGroupColumns}
          autoHeight={true}
          density="compact"
          hideFooterSelectedRowCount={true}
          loading={props.isListingGuidance || props.isUpdatingGuidance}
          sx={{ marginTop: "20px" }}
        />
        <Typography variant="h6" sx={{ mt: 3 }}>Talent Themes</Typography>
        <DataGrid
          rows={talentThemeGuidance}
          columns={talentThemeColumns}
          autoHeight={true}
          density="compact"
          hideFooterSelectedRowCount={true}
          loading={props.isListingGuidance || props.isUpdatingGuidance}
          sx={{ marginTop: "20px" }}
        />
      </Container>
      {guidanceFile &&
        <Dialog
          open={isConfirmationDialogOpen.value}
          onClose={handleConfirmationDialogClose}
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <DialogTitle id="confirmation-dialog-title">
            Update Guidance Database
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirmation-dialog-description">
              You are about to update the guidance database with "{guidanceFile.name}".
              Existing database will be overwritten.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleContinueButtonClick}
              sx={{ mt: 1, mr: 1 }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              onClick={handleConfirmationDialogClose}
              sx={{ mt: 1, mr: 1 }}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
}

const mapState = (state) => {
  const {
    isListingGuidance,
    guidance,
    isUpdatingGuidance,
  } = state.hrc;
  return {
    isListingGuidance,
    guidance,
    isUpdatingGuidance,
    alert: state.alert,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  listGuidance: hrcActions.listHrcGuidance,
  updateGuidance: hrcActions.updateHrcGuidance,
};

export default connect(mapState, actionCreators)(CoupleGuidanceDatabasePage);
